import { useQuery } from '@apollo/client/react';
import { Color, Icons, InputField, mediaQuery, RddkTheme, Container as RduiContainer, Typography } from 'db-npm-e4sc-rd-ui';
import gql from 'graphql-tag';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Section } from '../../shared/components';
import { DEFAULT_ROOT_PATH } from './config';
import { FilterChip } from './FilterChip';

// React 19 / db-npm-e4sc-rd-ui type incompatibility — pre-existing across all rdui components in this project
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Container = RduiContainer as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const InputFieldComp = InputField as any;

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const ARTICLE_LIST_QUERY = gql`
  query ArticleListQuery($rootPath: String!, $language: String!) {
    item(path: $rootPath, language: $language) {
      children {
        name
        children {
          name
          url
          field(name: "Meta Title") {
            value
          }
        }
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ArticleItem = {
  name: string;
  url: string;
  title: string;
  date: Date | null;
  dateFormatted: string;
};

type YearGroup = {
  year: string;
  articles: ArticleItem[];
};

// ---------------------------------------------------------------------------
// Date parsing
// Numbers after the non-digit prefix are either:
//   YYYYMMDD  (e.g. sm20240102  → 2024-01-02)
//   DDMMYYYY  (e.g. pr02022024  → 2024-02-02)
// ---------------------------------------------------------------------------

function parseArticleDate(name: string): { date: Date | null; formatted: string } {
  const digits = name.replace(/^[a-zA-Z]+/, '');
  if (digits.length < 8) return { date: null, formatted: '' };

  const first8 = digits.slice(0, 8);
  let year: number, month: number, day: number;

  if (/^20\d{2}/.test(first8)) {
    // YYYYMMDD
    year = parseInt(first8.slice(0, 4), 10);
    month = parseInt(first8.slice(4, 6), 10);
    day = parseInt(first8.slice(6, 8), 10);
  } else {
    // DDMMYYYY
    day = parseInt(first8.slice(0, 2), 10);
    month = parseInt(first8.slice(2, 4), 10);
    year = parseInt(first8.slice(4, 8), 10);
  }

  if (!year || !month || !day) return { date: null, formatted: '' };

  const date = new Date(year, month - 1, day);
  const formatted = `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`;
  return { date, formatted };
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

// Year filter group — FilterChipGroup layout with context-specific margin
const YearFilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
  margin-bottom: 32px;
`;

// Count shown in accordion year header
const YearCount = styled.span`
  font-weight: 400;
  font-size: 13px;
  margin-left: 6px;
  opacity: 0.8;
`;

// Search input wrapper — constrains width and provides bottom margin
const SearchWrapper = styled.div`
  margin-bottom: 32px;
`;

const AccordionSection = styled.div`
  border-top: 1px solid ${Color.rdGrey};

  &:last-child {
    border-bottom: 1px solid ${Color.rdGrey};
  }
`;

const AccordionHeader = styled.button<{ open: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px 16px 20px 20px;
  background: ${(p) => (p.open ? Color.whiteSmoke : 'transparent')};
  border: none;
  border-left: 4px solid ${(p) => (p.open ? Color.rdRed : 'transparent')};
  cursor: pointer;
  text-align: left;
  font-family: ${RddkTheme.fonts.sans};
  transition: background 0.2s ease, border-left-color 0.3s ease;

  &:hover h3,
  &:focus h3 {
    color: ${Color.rdRed};
  }
`;

const ChevronIcon = styled.span<{ open: boolean }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: ${(p) => (p.open ? 'rotate(-135deg)' : 'rotate(45deg)')};
  transition: transform 0.2s;
  margin-right: 4px;
  flex-shrink: 0;
`;

const ArticleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 8px;
  margin-left: 24px;
`;

const ArticleRow = styled.tr`
  border-top: 1px solid #f0f0f0;

  &:first-child {
    border-top: none;
  }
`;

const DateCell = styled.td`
  width: 110px;
  padding: 12px 16px 12px 0;
  font-family: ${RddkTheme.fonts.sans};
  font-size: 13px;
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  vertical-align: top;

  ${mediaQuery.sm} {
    width: 130px;
  }
`;

const TitleCell = styled.td`
  padding: 12px 0;
  vertical-align: top;
`;

const ArticleLink = styled.a`
  font-family: ${RddkTheme.fonts.sans};
  font-size: 14px;
  color: ${Color.rdPureBlack};
  text-decoration: none;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 1px;
    background: ${Color.rdRed};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.2s;
  }

  &:hover::after,
  &:focus::after {
    transform: scaleX(1);
  }

  &:hover,
  &:focus {
    color: ${Color.rdRed};
  }
`;

const ShowMoreButton = styled.button`
  margin: 8px 0 16px;
  padding: 8px 20px;
  font-family: ${RddkTheme.fonts.sans};
  font-size: 14px;
  font-weight: 600;
  color: ${Color.rdRed};
  background: none;
  border: 2px solid ${Color.rdRed};
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover,
  &:focus {
    background: ${Color.rdRed};
    color: #fff;
  }
`;

const NoResultsText = styled.p`
  font-family: ${RddkTheme.fonts.sans};
  font-size: 14px;
  color: ${Color.rdBlackTint2};
  padding: 12px 0;
`;

const StatusBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const StatusText = styled.p`
  font-family: ${RddkTheme.fonts.sans};
  font-size: 14px;
  color: ${Color.rdBlackTint2};
  margin: 0;
`;

const InnerWrapper = styled.div`
  max-width: 50%;
  margin-left: auto;
  margin-right: auto;
`;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PAGE_SIZE = 10;

type YearSectionProps = {
  group: YearGroup;
  searchQuery: string;
  initiallyOpen: boolean;
};

const YearSection: React.FC<YearSectionProps> = ({ group, searchQuery, initiallyOpen }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(initiallyOpen);
  const [showAll, setShowAll] = useState(false);

  // Åbn accordionen når en FilterChip vælges (initiallyOpen skifter til true på et allerede mountet komponent)
  useEffect(() => {
    if (initiallyOpen) setOpen(true);
  }, [initiallyOpen]);

  const filtered = useMemo(() => {
    if (!searchQuery) return group.articles;
    const q = searchQuery.toLowerCase();
    return group.articles.filter((a) => a.title.toLowerCase().includes(q) || a.dateFormatted.includes(q));
  }, [group.articles, searchQuery]);

  // Auto-open when search has results — regardless of manual open/close state
  const effectivelyOpen = searchQuery ? filtered.length > 0 : open;

  const visible = showAll || searchQuery ? filtered : filtered.slice(0, PAGE_SIZE);
  const hasMore = !showAll && !searchQuery && filtered.length > PAGE_SIZE;

  return (
    <AccordionSection>
      <AccordionHeader open={effectivelyOpen} onClick={() => setOpen((o) => !o)} aria-expanded={effectivelyOpen}>
        <Typography type="h6" as="h3" color={Color.rdPureBlack}>
          {group.year}
          <YearCount>({group.articles.length})</YearCount>
        </Typography>
        <ChevronIcon open={effectivelyOpen} />
      </AccordionHeader>

      {effectivelyOpen && (
        <>
          {filtered.length === 0 ? (
            <NoResultsText>{t('article-list-no-results-year')}</NoResultsText>
          ) : (
            <ArticleTable>
              <tbody>
                {visible.map((article) => (
                  <ArticleRow key={article.name}>
                    <DateCell>{article.dateFormatted}</DateCell>
                    <TitleCell>
                      <ArticleLink href={article.url}>{article.title || article.name}</ArticleLink>
                    </TitleCell>
                  </ArticleRow>
                ))}
              </tbody>
            </ArticleTable>
          )}
          {hasMore && (
            <ShowMoreButton onClick={() => setShowAll(true)}>
              {t('article-list-show-all', { count: filtered.length })}
            </ShowMoreButton>
          )}
        </>
      )}
    </AccordionSection>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type ArticleListProps = {
  fields?: any;
  rendering?: any;
};

const ArticleList: React.FC<ArticleListProps> = ({ fields = {} }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeYear, setActiveYear] = useState<string | null>(null);

  // Root Path: JSS Layout Service returnerer Droptree-felt som item-objekt direkte (ingen .value wrapper).
  // I disconnected mode (YAML) er feltet { value: "string" }.
  // GraphQL item(path:) accepterer GUIDs på formen {guid}.
  const rootPathField = fields['Root Path'] as any;
  const rootPath = rootPathField?.id
    ? `{${rootPathField.id}}` // Connected: Droptree → GUID
    : typeof rootPathField?.value === 'string' && rootPathField.value
    ? rootPathField.value // Disconnected: plain string
    : DEFAULT_ROOT_PATH; // Fallback

  const { data, loading, error } = useQuery<any>(ARTICLE_LIST_QUERY, {
    variables: { rootPath, language: 'da' },
  });

  const yearGroups: YearGroup[] = useMemo(() => {
    if (!data?.item?.children) return [];

    return data.item.children
      .filter((yearNode: any) => /^\d{4}$/.test(yearNode.name))
      .map((yearNode: any) => {
        const articles: ArticleItem[] = (yearNode.children || [])
          .filter((child: any) => child.name !== 'Local Components')
          .map((child: any) => {
            const { date, formatted } = parseArticleDate(child.name);
            return {
              name: child.name,
              url: child.url,
              title: child.field?.value || child.name,
              date,
              dateFormatted: formatted,
            };
          })
          .sort((a: ArticleItem, b: ArticleItem) => {
            if (!a.date || !b.date) return 0;
            return b.date.getTime() - a.date.getTime();
          });

        return { year: yearNode.name, articles };
      })
      .sort((a: YearGroup, b: YearGroup) => parseInt(b.year, 10) - parseInt(a.year, 10));
  }, [data]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery) return yearGroups;
    const q = searchQuery.toLowerCase();
    return yearGroups
      .map((g) => ({
        ...g,
        articles: g.articles.filter((a) => a.title.toLowerCase().includes(q) || a.dateFormatted.includes(q)),
      }))
      .filter((g) => g.articles.length > 0);
  }, [yearGroups, searchQuery]);

  const displayedGroups = useMemo(() => {
    if (activeYear) return filteredGroups.filter((g) => g.year === activeYear);
    return filteredGroups;
  }, [filteredGroups, activeYear]);

  if (loading || (!data && !error)) {
    return (
      <Section componentName="ArticleList" margin="large">
        <Container>
          <InnerWrapper>
            <StatusBox>
              <Icons.Spinner />
              <StatusText>{t('article-list-loading')}</StatusText>
            </StatusBox>
          </InnerWrapper>
        </Container>
      </Section>
    );
  }

  if (error || !data?.item) {
    return (
      <Section componentName="ArticleList" margin="large">
        <Container>
          <InnerWrapper>
            <StatusBox>
              <Icons.NoConnection />
              <StatusText>{t('article-list-error')}</StatusText>
            </StatusBox>
          </InnerWrapper>
        </Container>
      </Section>
    );
  }

  return (
    <Section componentName="ArticleList" margin="large">
      <Container>
        <InnerWrapper>
          {/* Search */}
          <SearchWrapper>
            <InputFieldComp
              label={t('article-list-search-placeholder') as string}
              aria-label={t('article-list-search-placeholder') as string}
              layout="white"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchQuery(e.target.value);
                setActiveYear(null);
              }}
              onReset={() => {
                setSearchQuery('');
                setActiveYear(null);
              }}
            />
          </SearchWrapper>

          {/* Year filter chips */}
          {!searchQuery && (
            <YearFilterGroup>
              <FilterChip selected={activeYear === null} onClick={() => setActiveYear(null)}>
                {t('article-list-all-years')}
              </FilterChip>
              {yearGroups.map((g) => (
                <FilterChip
                  key={g.year}
                  selected={activeYear === g.year}
                  onClick={() => setActiveYear(activeYear === g.year ? null : g.year)}
                >
                  {g.year}
                </FilterChip>
              ))}
            </YearFilterGroup>
          )}

          {/* Article accordion */}
          {displayedGroups.length === 0 ? (
            <NoResultsText>{t('article-list-no-results', { query: searchQuery })}</NoResultsText>
          ) : (
            displayedGroups.map((group, i) => (
              <YearSection
                key={group.year}
                group={group}
                searchQuery={searchQuery}
                initiallyOpen={i === 0 || !!activeYear}
              />
            ))
          )}
        </InnerWrapper>
      </Container>
    </Section>
  );
};

export default ArticleList;
